import { Dayjs } from 'dayjs';

export type MeteoWarningLevel = 'none' | 'orange' | 'red';

export interface HourlyWeatherData {
  time: string;
  hour: string;
  temperature: number;
  windSpeed: number;
  windGusts: number;
  precipitation: number;
  adverseSeverity?: MeteoWarningLevel;
}

export interface NightSection {
  date: Dayjs;
  hours: HourlyWeatherData[];
  warnings: MeteoWarningLevel[];
}

export type MeteoMetric = 'temperature' | 'wind' | 'gusts' | 'precipitation';

interface Thresholds {
  orange: number;
  red: number;
}

const WIND_THRESHOLDS: Thresholds = { orange: 30, red: 50 };
const GUST_THRESHOLDS: Thresholds = { orange: 40, red: 60 };
const PRECIPITATION_THRESHOLDS: Thresholds = { orange: 2, red: 5 };
const TEMPERATURE_COLD_THRESHOLDS: Thresholds = { orange: 0, red: -5 };

export const getMetricWarningLevel = (metric: MeteoMetric, value: number): MeteoWarningLevel => {
  switch (metric) {
    case 'wind': {
      if (value >= WIND_THRESHOLDS.red) return 'red';
      if (value >= WIND_THRESHOLDS.orange) return 'orange';
      return 'none';
    }
    case 'gusts': {
      if (value >= GUST_THRESHOLDS.red) return 'red';
      if (value >= GUST_THRESHOLDS.orange) return 'orange';
      return 'none';
    }
    case 'precipitation': {
      if (value >= PRECIPITATION_THRESHOLDS.red) return 'red';
      if (value >= PRECIPITATION_THRESHOLDS.orange) return 'orange';
      return 'none';
    }
    case 'temperature': {
      if (value <= TEMPERATURE_COLD_THRESHOLDS.red) return 'red';
      if (value <= TEMPERATURE_COLD_THRESHOLDS.orange) return 'orange';
      return 'none';
    }
    default:
      return 'none';
  }
};

export const getHourWarningLevel = (hour: HourlyWeatherData): MeteoWarningLevel => {
  if (hour.adverseSeverity !== undefined) return hour.adverseSeverity;
  const levels: MeteoWarningLevel[] = [
    getMetricWarningLevel('wind', hour.windSpeed),
    getMetricWarningLevel('gusts', hour.windGusts),
    getMetricWarningLevel('precipitation', hour.precipitation),
  ];
  if (levels.includes('red')) return 'red';
  if (levels.includes('orange')) return 'orange';
  return 'none';
};

export const getSectionWarnings = (hours: HourlyWeatherData[]): MeteoWarningLevel[] =>
  [...new Set(hours.map(getHourWarningLevel))].filter((l) => l !== 'none');

export const WARNING_COLORS = {
  none: '#4592FA',
  orange: '#FFB147',
  red: '#E9554A',
} as const;

// Thresholds mirror FacadeWeatherRules on the backend (corrected values already applied).
export const BACKEND_PRECIPITATION_THRESHOLD = 1.0;
export const BACKEND_WIND_THRESHOLD = 8.0;
export const BACKEND_GUST_THRESHOLD = 14.0;

export const getBadgeIconName = (hours: HourlyWeatherData[]): string => {
  if (hours.length === 0) return 'cloudy';

  const rankMap: Record<MeteoWarningLevel, number> = { none: 0, orange: 1, red: 2 };
  const worstHour = hours.reduce((prev, curr) =>
    rankMap[getHourWarningLevel(curr)] >= rankMap[getHourWarningLevel(prev)] ? curr : prev,
  );

  if (getHourWarningLevel(worstHour) === 'none') return 'clear';

  const useBackendThresholds = worstHour.adverseSeverity !== undefined;
  const hasPrecip = useBackendThresholds
    ? worstHour.precipitation > BACKEND_PRECIPITATION_THRESHOLD
    : getMetricWarningLevel('precipitation', worstHour.precipitation) !== 'none';
  const hasGusts = useBackendThresholds
    ? worstHour.windGusts > BACKEND_GUST_THRESHOLD
    : getMetricWarningLevel('gusts', worstHour.windGusts) !== 'none';
  const hasWind = useBackendThresholds
    ? worstHour.windSpeed > BACKEND_WIND_THRESHOLD
    : getMetricWarningLevel('wind', worstHour.windSpeed) !== 'none';
  const hasTemp = worstHour.temperature < 0;

  if (hasPrecip) return 'rain';
  if (hasGusts) return 'gusts';
  if (hasWind) return 'wind';
  if (hasTemp) return 'cold';

  return 'cloudy';
};
